import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { CATALOGUE, findModel } from '../data/catalogue';
import { BUSINESS } from '../data/pricing';
import { lookupPincode, isValidPincode } from '../data/pincodes';
import { priceOf, formatINR, newQuoteRef } from '../lib/quote';
import { saveLead } from '../lib/api';
import { IMG_FALLBACK } from '../lib/images';

// Route: /request-unit?model=<slug>&kit=<withKit|withoutKit>
//
// Units that are not in the godown are not sold online. They are a request:
// three fields, a lead, and a phone call. No payment, no price held, no expiry
// to honour, no balance to chase.
//
// The price is shown as indicative on purpose. It is today's price on a unit
// that has to come from Daikin, and it is confirmed on the call — which is
// the honest position, and the one that scales.

const input = (bad) =>
  `mt-1 w-full rounded-lg border px-3 py-3 text-[15px] outline-none transition ${
    bad ? 'border-red-400 bg-red-50/40' : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
  }`;

export default function RequestUnit() {
  const [params] = useSearchParams();
  const slug = params.get('model') || '';
  const kitParam = params.get('kit');

  // The URL may carry either a slug (from a product page link) or a model id.
  // findModel() only knows ids, which is why a slug came back empty.
  const model = useMemo(
    () => findModel(slug) || CATALOGUE.find((m) => m.slug === slug) || null,
    [slug],
  );

  const kit = model && model.variants[kitParam]
    ? kitParam
    : model && model.variants.withKit ? 'withKit' : 'withoutKit';

  const price = model ? priceOf(model.id, kit) : null;

  const [form, setForm] = useState({ mobile: '', pincode: '', fullName: '', email: '', notes: '' });
  const [touched, setTouched] = useState(false);
  const [ref, setRef] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const digits = (k, max) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value.replace(/\D/g, '').slice(0, max) }));

  const pin = useMemo(
    () => (form.pincode.length === 6 ? lookupPincode(form.pincode) : null),
    [form.pincode],
  );

  const err = (k) => {
    if (!touched) return '';
    if (k === 'mobile' && !/^[6-9]\d{9}$/.test(form.mobile)) return 'Enter a 10-digit mobile number';
    if (k === 'pincode') {
      if (!isValidPincode(form.pincode)) return 'Enter a 6-digit pincode';
      if (pin && !pin.ok) return pin.message || 'We do not deliver to this pincode';
    }
    if (k === 'fullName' && form.fullName.trim().length < 2) return 'Enter your name';
    if (k === 'email' && form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return 'Check this email address';
    return '';
  };

  const valid = ['mobile', 'pincode', 'fullName', 'email'].every((k) => {
    if (k === 'mobile') return /^[6-9]\d{9}$/.test(form.mobile);
    if (k === 'pincode') return isValidPincode(form.pincode) && pin && pin.ok;
    if (k === 'fullName') return form.fullName.trim().length >= 2;
    return !form.email || /^\S+@\S+\.\S+$/.test(form.email);
  });

  const unitLine = model
    ? `${model.title} (${model.variants[kit].idu}, ${kit === 'withKit' ? 'with kit' : 'without kit'})`
    : 'a Daikin unit';

  const whatsappHref = () => {
    const text = [
      "Hi, I'd like to order a unit from citicool.in",
      '',
      unitLine,
      ref ? `Reference: ${ref}` : '',
      form.fullName ? `Name: ${form.fullName}` : '',
      form.pincode ? `Pincode: ${form.pincode}` : '',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(text)}`;
  };

  const submit = async () => {
    setTouched(true);
    if (!valid || sending) return;
    setSending(true);
    const r = newQuoteRef();
    try {
      await saveLead({
        ref: r,
        kind: 'request',
        name: form.fullName,
        mobile: form.mobile,
        email: form.email,
        pincode: form.pincode,
        notes: [unitLine, form.notes].filter(Boolean).join(' — '),
        items: unitLine,
      });
    } catch {
      // The lead may not have saved, but the customer still gets a reference
      // and a WhatsApp route through. Losing the enquiry to a network blip is
      // worse than storing it twice.
    }
    setRef(r);
    setSending(false);
  };

  if (!model) {
    return (
      <>
        <Helmet>
          <title>Request a unit — Citi Cool</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <main className="mx-auto max-w-2xl px-4 pt-24 pb-16 text-center lg:pt-28">
          <h1 className="text-xl font-semibold text-gray-900">Which unit did you want?</h1>
          <p className="mt-2 text-sm text-gray-600">
            Pick it from the price list and we&apos;ll take it from there.
          </p>
          <Link to="/daikin-ac-price-list-delhi"
            className="mt-5 inline-block rounded-md bg-blue-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-700">
            Browse Daikin ACs
          </Link>
        </main>
      </>
    );
  }

  if (ref) {
    return (
      <>
        <Helmet>
          <title>Request received — Citi Cool</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <main className="mx-auto max-w-2xl px-4 pt-24 pb-16 lg:pt-28">
          <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4">
            <p className="text-sm font-medium text-green-900">Request received</p>
            <p className="mt-1 text-sm text-green-800">
              Reference <span className="font-mono font-semibold">{ref}</span>. We&apos;ll call you
              on {form.mobile} to confirm the price and a delivery date, usually the same day.
            </p>
          </div>
          <a href={whatsappHref()} target="_blank" rel="noopener noreferrer"
            className="mt-5 block rounded-lg bg-green-600 px-5 py-4 text-center text-base font-medium text-white transition hover:bg-green-700">
            Send this on WhatsApp instead
          </a>
          <p className="mt-3 text-center text-sm text-gray-600">
            Or call <a href={`tel:${BUSINESS.phone}`} className="font-medium text-blue-700">{BUSINESS.phoneDisplay}</a>
          </p>
          <Link to="/daikin-ac-price-list-delhi"
            className="mt-6 block text-center text-sm text-blue-700 underline underline-offset-2">
            Back to the price list
          </Link>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Request {model.title} — Citi Cool</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <main className="mx-auto max-w-2xl px-4 pt-24 pb-16 lg:pt-28">
        <h1 className="text-xl font-semibold text-gray-900">Request this unit</h1>
        <p className="mt-1.5 text-sm text-gray-600">
          This one isn&apos;t in our godown today, so we order it from Daikin. Leave your number
          and we&apos;ll confirm the price and a date, usually within the hour.
        </p>

        <div className="mt-5 flex gap-3 rounded-xl border border-gray-200 bg-white p-3">
          <img src={model.image || IMG_FALLBACK} alt={model.title} width="72" height="72"
            className="h-18 w-18 shrink-0 rounded-lg bg-gray-50 object-contain" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">{model.title}</p>
            <p className="mt-0.5 text-[12px] text-gray-500">
              {model.variants[kit].idu} · {kit === 'withKit' ? 'with piping kit' : 'without piping kit'}
            </p>
            {price != null && (
              <p className="mt-1 text-sm text-gray-900">
                {formatINR(price)}
                <span className="ml-1.5 text-[12px] font-normal text-gray-500">
                  indicative, confirmed on the call
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">Mobile number</span>
            <input inputMode="numeric" value={form.mobile} onChange={digits('mobile', 10)}
              autoComplete="tel" className={input(Boolean(err('mobile')))} />
            {err('mobile') && <span className="mt-1 block text-[11px] text-red-600">{err('mobile')}</span>}
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">Pincode</span>
            <input inputMode="numeric" value={form.pincode} onChange={digits('pincode', 6)}
              autoComplete="postal-code" className={input(Boolean(err('pincode')))} />
            {err('pincode')
              ? <span className="mt-1 block text-[11px] text-red-600">{err('pincode')}</span>
              : pin && pin.ok
                ? <span className="mt-1 block text-[11px] text-gray-500">{pin.city}{pin.state ? `, ${pin.state}` : ''}</span>
                : null}
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">Name</span>
            <input value={form.fullName} onChange={set('fullName')} autoComplete="name"
              className={input(Boolean(err('fullName')))} />
            {err('fullName') && <span className="mt-1 block text-[11px] text-red-600">{err('fullName')}</span>}
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">
              Email <span className="ml-1 font-normal text-gray-400">optional</span>
            </span>
            <input type="email" value={form.email} onChange={set('email')} autoComplete="email"
              className={input(Boolean(err('email')))} />
            {err('email') && <span className="mt-1 block text-[11px] text-red-600">{err('email')}</span>}
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-gray-700">
              Anything we should know <span className="ml-1 font-normal text-gray-400">optional</span>
            </span>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              placeholder="Installation needed, preferred day, floor" className={input(false)} />
          </label>

          <button type="button" onClick={submit} disabled={sending}
            className="w-full rounded-lg bg-blue-600 px-5 py-4 text-base font-medium text-white transition hover:bg-blue-700 disabled:opacity-70">
            {sending ? 'Sending…' : 'Request this unit'}
          </button>

          <p className="text-center text-[11px] text-gray-500">
            No payment now. We confirm the price and the date before anything is charged.
          </p>
        </div>
      </main>
    </>
  );
}